import React, { useState, useEffect } from 'react';
import {
  Box,
  Modal,
  ModalDialog,
  Typography,
  Button,
  Table,
  Input,
  FormControl,
  FormLabel,
  Alert,
  Card
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';

const DisposablePopup = ({ disposableId, onClose }) => {
  const [disposable, setDisposable] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantityChange, setQuantityChange] = useState('');
  const [usageStartDate, setUsageStartDate] = useState('');
  const [usageEndDate, setUsageEndDate] = useState('');
  const [usage, setUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  useEffect(() => {
    fetchDisposable();
    fetchHistory();
  }, [disposableId]);

  const fetchDisposable = async () => {
    try {
      const response = await axiosInstance.get(`/disposables/${disposableId}`);
      setDisposable(response.data.disposable);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch disposable');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axiosInstance.get(`/disposables/${disposableId}/history`);
      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  const handleQuantityUpdate = async (change) => {
    try {
      await axiosInstance.put(`/disposables/${disposableId}/quantity`, {
        changeAmount: change
      });
      await fetchDisposable();
      await fetchHistory();
      setQuantityChange('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update quantity');
    }
  };

  const calculateUsage = async () => {
    if (!usageStartDate || !usageEndDate) {
      setError('Please select both start and end dates');
      return;
    }

    try {
      setLoadingUsage(true);
      const response = await axiosInstance.get(`/disposables/${disposableId}/usage`, {
        params: {
          startDate: usageStartDate,
          endDate: usageEndDate
        }
      });
      setUsage(response.data.totalUsage);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to calculate usage');
    } finally {
      setLoadingUsage(false);
    }
  };

  if (loading) {
    return (
      <Modal open={true} onClose={onClose}>
        <ModalDialog>
          <Typography>Loading...</Typography>
        </ModalDialog>
      </Modal>
    );
  }

  return (
    <Modal open={true} onClose={onClose}>
      <ModalDialog
        sx={{
          maxWidth: '90vw',
          width: '1000px',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography level="h2" sx={{ color: '#eb7a24' }}>
            {disposable?.disposable_name} - Details
          </Typography>
          <Button variant="plain" onClick={onClose}>
            Close
          </Button>
        </Box>

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 3, p: 2 }}>
          <Typography level="h3" sx={{ mb: 2 }}>
            Current Information
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Typography>
              <strong>Vendor:</strong> {disposable?.vendor_name || 'N/A'}
            </Typography>
            <Typography>
              <strong>Rate (per item):</strong> {disposable?.rate != null ? Number(disposable.rate).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}
            </Typography>
            <Typography>
              <strong>Quantity:</strong> {disposable?.quantity}
            </Typography>
            <Typography>
              <strong>Date Entered:</strong>{' '}
              {disposable?.date_entered
                ? new Date(disposable.date_entered).toLocaleDateString()
                : 'N/A'}
            </Typography>
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <FormControl sx={{ width: 150 }}>
              <FormLabel>Change Amount</FormLabel>
              <Input
                type="number"
                value={quantityChange}
                onChange={(e) => setQuantityChange(e.target.value)}
                placeholder="Amount"
                sx={{
                  '&:hover': { borderColor: '#ea7a23' },
                  '&:focus-within': { borderColor: '#ea7a23' }
                }}
              />
            </FormControl>
            <Button
              variant="outlined"
              color="success"
              onClick={() => handleQuantityUpdate(parseInt(quantityChange || 0))}
              disabled={!quantityChange || parseInt(quantityChange) === 0}
            >
              Increase
            </Button>
            <Button
              variant="outlined"
              color="danger"
              onClick={() => handleQuantityUpdate(-Math.abs(parseInt(quantityChange || 0)))}
              disabled={!quantityChange || parseInt(quantityChange) === 0}
            >
              Decrease
            </Button>
          </Box>
        </Card>

        <Card sx={{ mb: 3, p: 2 }}>
          <Typography level="h3" sx={{ mb: 2 }}>
            Usage Tracker
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl>
              <FormLabel>Start Date</FormLabel>
              <Input
                type="date"
                value={usageStartDate}
                onChange={(e) => setUsageStartDate(e.target.value)}
                sx={{
                  '&:hover': { borderColor: '#ea7a23' },
                  '&:focus-within': { borderColor: '#ea7a23' }
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel>End Date</FormLabel>
              <Input
                type="date"
                value={usageEndDate}
                onChange={(e) => setUsageEndDate(e.target.value)}
                sx={{
                  '&:hover': { borderColor: '#ea7a23' },
                  '&:focus-within': { borderColor: '#ea7a23' }
                }}
              />
            </FormControl>
            <Button
              onClick={calculateUsage}
              loading={loadingUsage}
              sx={{
                backgroundColor: '#eb7a24',
                '&:hover': { backgroundColor: '#cc6a1f' }
              }}
            >
              Calculate Usage
            </Button>
          </Box>
          {usage !== null && (
            <Alert color="success">
              Total Usage: {usage} units
            </Alert>
          )}
        </Card>

        <Card>
          <Typography level="h3" sx={{ mb: 2 }}>
            Change History
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table
              sx={{
                '& thead th': {
                  backgroundColor: '#eb7a24',
                  color: 'white',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                },
                '& tbody tr:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9'
                },
                '& tbody tr:hover': {
                  backgroundColor: '#fff4e6'
                },
                '& td': {
                  wordWrap: 'break-word',
                  whiteSpace: 'normal'
                }
              }}
            >
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Vendor</th>
                  <th>Quantity Change</th>
                  <th>New Quantity</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                      No history found
                    </td>
                  </tr>
                ) : (
                  history.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {new Date(item.date).toLocaleDateString()}
                      </td>
                      <td>{item.vendor_name || 'N/A'}</td>
                      <td
                        style={{
                          color: item.change_amount > 0 ? 'green' : 'red',
                          fontWeight: 'bold'
                        }}
                      >
                        {item.change_amount > 0 ? '+' : ''}
                        {item.change_amount}
                      </td>
                      <td>{item.new_quantity}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Box>
        </Card>
      </ModalDialog>
    </Modal>
  );
};

export default DisposablePopup;

